/**
 * PM Match Card - Unified Match Card Component
 * 
 * Componente Lit unificato per visualizzare le card dei match in tutti i bracket types.
 * Design moderno e simmetrico con supporto per:
 * - Match completati con punteggio
 * - Match programmati
 * - Match TBD
 * - Skill levels
 * - Progression info (Double Elimination)
 * - Dark mode
 * - Responsive mobile-first
 * 
 * NOTA: I nomi dei giocatori devono essere separati da line break (\n) per
 * visualizzarli su righe separate senza il carattere "+".
 * 
 * @example
 * <pm-match-card
 *   match-id="1"
 *   match-number="Match 1"
 *   status="completed"
 *   side1-name="Marco Rossi
 * Luca Verdi"
 *   side2-name="Paolo Bianchi
 * Andrea Neri"
 *   winner-score="6-3, 4-6, 7-5"
 *   side1-is-winner
 *   editable
 *   edit-url="/match/1/edit/"
 * ></pm-match-card>
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { MatchStatus, SkillLevel, CurrentPlayers } from '../../types/match';

@customElement('pm-match-card')
export class PmMatchCard extends LitElement {
  static styles = css`
    /* ===== UNIFIED MATCH CARD - MODERN SYMMETRIC VERSION ===== */
    :host {
      display: block;
    }

    .pm-match-card {
      border: 2px solid #dee2e6;
      border-radius: 12px;
      padding: 0;
      background: white;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .pm-match-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    }

    .pm-match-card.completed {
      border-color: #198754;
    }

    .pm-match-card.scheduled {
      border-color: #ffc107;
    }

    .pm-match-card.in_progress {
      border-color: #0dcaf0;
    }

    /* Header moderno con gradient */
    .pm-match-card__header {
      background: linear-gradient(165deg, #f8f9fa 0%, #e9ecef 100%);
      padding: 12px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 2px solid #dee2e6;
    }

    .pm-match-card.completed .pm-match-card__header {
      background: linear-gradient(165deg, #d1e7dd 0%, #a3cfbb 100%);
    }

    .pm-match-card.scheduled .pm-match-card__header {
      background: linear-gradient(165deg, #fff3cd 0%, #ffe69c 100%);
    }

    .pm-match-card.in_progress .pm-match-card__header {
      background: linear-gradient(165deg, #cff4fc 0%, #9eeaf9 100%);
    }

    .pm-match-card__number {
      font-weight: 700;
      color: #212529;
      font-size: 0.9rem;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    /* Teams container - DIAGONAL LAYOUT */
    .pm-match-card__teams {
      position: relative;
      padding: 0;
      min-height: 140px;
      display: grid;
      grid-template-rows: 1fr 1fr;
      overflow: hidden;
    }

    /* Campo da Padel - Sfondo azzurro come campo.html */
    .pm-match-card__teams::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      background: rgba(79, 167, 219, 0.3); /* #4fa7db azzurro trasparente */
      pointer-events: none;
      z-index: 1;
    }

    /* Linee del campo come div HTML */
    .pm-match-card__court-line {
      position: absolute;
      background: white;
      pointer-events: none;
      z-index: 2;
    }

    /* Rete centrale VERTICALE - NERA */
    .pm-match-card__court-net {
      position: absolute;
      background: white;
      pointer-events: none;
      z-index: 3;
      top: 0;
      bottom: 0;
      left: 50%;
      width: 2px;
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
      transform: translateX(-50%);
    }

    /* Linee verticali di servizio */
    .pm-match-card__court-line--service-left {
      top: 0;
      bottom: 0;
      left: 25%;
      width: 2px;
      transform: translateX(-50%);
      opacity: 0.7;
    }

    .pm-match-card__court-line--service-right {
      top: 0;
      bottom: 0;
      left: 75%;
      width: 2px;
      transform: translateX(-50%);
      opacity: 0.7;
    }

    /* Linea orizzontale centrale - solo tra le linee di servizio */
    .pm-match-card__court-line--center-horizontal {
      top: 50%;
      left: 25%;
      right: 25%;
      height: 2px;
      transform: translateY(-50%);
      opacity: 0.6;
    }

    /* Team containers - CAMPO DA PADEL ORIZZONTALE */
    .pm-match-card__team {
      display: flex;
      justify-content: center;
      padding: 12px 16px;
      position: relative;
      z-index: 3;
      gap: 8px;
      background: rgba(0, 0, 0, 0.02); /* Leggera ombra per profondità */
    }

    /* Coppia 1 - METÀ CAMPO SINISTRA */
    .pm-match-card__team--left {
      align-items: center;
      text-align: left;
      justify-content: flex-start;
      padding-right: 32px;
    }

    /* Coppia 2 - METÀ CAMPO DESTRA */
    .pm-match-card__team--right {
      align-items: center;
      text-align: right;
      justify-content: flex-end;
      padding-left: 32px;
    }

    /* Team name row - NOME + BADGE LIVELLO - DRITTO */
    .pm-match-card__team-name-row {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
    }

    /* Coppia 1 - Badge a SINISTRA del nome (BADGE + NOME) */
    .pm-match-card__team--left .pm-match-card__team-name-row {
      flex-direction: row; /* Badge → Nome */
      justify-content: flex-start;
    }

    /* Coppia 2 - Badge a DESTRA del nome (NOME + BADGE) */
    .pm-match-card__team--right .pm-match-card__team-name-row {
      flex-direction: row; /* Nome → Badge */
      justify-content: flex-end;
    }

    /* Team names - CLEAN TYPOGRAPHY */
    .pm-match-card__team-name {
      font-size: 1rem;
      line-height: 1.4;
      font-weight: 600;
      color: #212529;
      white-space: pre-line;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;
      hyphens: auto;
      flex: 1;
      min-width: 0;
      letter-spacing: 0.3px;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }

    .pm-match-card__team.winner .pm-match-card__team-name {
      font-weight: 800;
      color: #198754;
      text-shadow: 0 2px 4px rgba(25, 135, 84, 0.2);
    }

    /* Skills container - INLINE orizzontale */
    .pm-match-card__team-skills {
      display: flex;
      flex-direction: column;
      gap: 4px;
      flex-shrink: 0;
      align-items: center;
    }

    .pm-match-card__team--left .pm-match-card__team-meta {
      justify-content: flex-start;
    }

    .pm-match-card__team--right .pm-match-card__team-meta {
      justify-content: flex-end;
    }

    /* VS Divider - PALLINA SULLA RETE VERTICALE */
    .pm-match-card__vs-container {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.25));
    }

    .pm-match-card__vs {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 900;
      font-size: 0.65rem;
      letter-spacing: 0.5px;
      background: 
        url('/static/img/padel-ball.png') center center / cover no-repeat;
      color: #1a5e1a;
      border: none;
      box-shadow: 
        0 3px 8px rgba(0, 0, 0, 0.3),
        0 1px 3px rgba(0, 0, 0, 0.2);
      text-shadow: 
        1px 1px 2px rgba(255, 255, 255, 0.8),
        -1px -1px 2px rgba(0, 0, 0, 0.4),
        0 0 3px rgba(255, 255, 255, 0.5);
    }

    /* Score Container - CENTERED */
    .pm-match-card__score-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      padding: 0 16px 12px 16px;
    }

    .pm-match-card__score {
      font-family: 'Courier New', monospace;
      font-size: 0.85rem;
      font-weight: 700;
      color: #198754;
      padding: 6px 12px;
      white-space: nowrap;
    }

    /* Winner indicator - TEAM 1 (LEFT) - STRISCIA VERTICALE CAMPO */
    .pm-match-card__team--left.winner::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: linear-gradient(180deg, 
        #ffd700 0%,     /* Oro */
        #ffed4e 50%,    /* Oro chiaro */
        #ffd700 100%    /* Oro */
      );
      border-radius: 0 8px 8px 0;
      box-shadow: 
        3px 0 10px rgba(255, 215, 0, 0.5),
        0 0 20px rgba(255, 215, 0, 0.3);
      animation: glow 2s ease-in-out infinite;
    }

    /* Winner indicator - TEAM 2 (RIGHT) - STRISCIA VERTICALE CAMPO */
    .pm-match-card__team--right.winner::before {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: linear-gradient(180deg, 
        #ffd700 0%,     /* Oro */
        #ffed4e 50%,    /* Oro chiaro */
        #ffd700 100%    /* Oro */
      );
      border-radius: 8px 0 0 8px;
      box-shadow: 
        -3px 0 10px rgba(255, 215, 0, 0.5),
        0 0 20px rgba(255, 215, 0, 0.3);
      animation: glow 2s ease-in-out infinite;
    }

    @keyframes glow {
      0%, 100% {
        opacity: 1;
        box-shadow: 
          0 3px 10px rgba(255, 215, 0, 0.5),
          0 0 20px rgba(255, 215, 0, 0.3);
      }
      50% {
        opacity: 0.8;
        box-shadow: 
          0 3px 15px rgba(255, 215, 0, 0.7),
          0 0 30px rgba(255, 215, 0, 0.5);
      }
    }

    /* Loser styling - OPACITY + BLUR */
    .pm-match-card__team.loser {
      opacity: 0.6;
      filter: grayscale(0.3);
    }

    .pm-match-card__team.loser .pm-match-card__team-name {
      text-decoration: line-through;
      text-decoration-thickness: 1px;
      text-decoration-color: rgba(0, 0, 0, 0.3);
    }

    /* Skill badges */
    .pm-match-card__skill {
      font-size: 0.65rem;
      padding: 3px 6px;
      border-radius: 4px;
      font-weight: 700;
      text-transform: uppercase;
      display: inline-block;
      width: fit-content;
    }

    .skill-principiante {
      background-color: #6c757d;
      color: white;
    }

    .skill-intermedio {
      background-color: #0dcaf0;
      color: white;
    }

    .skill-avanzato {
      background-color: #ffc107;
      color: #000;
    }

    .skill-esperto {
      background-color: #dc3545;
      color: white;
    }

    .skill-professionale {
      background: linear-gradient(165deg, #ffd700 0%, #ffed4e 100%);
      color: #000;
      font-weight: 900;
      box-shadow: 0 2px 4px rgba(255, 215, 0, 0.3);
    }

    /* Progression bar */
    .pm-match-card__progression {
      padding: 12px 16px;
      background: #f8f9fa;
      border-top: 1px solid #dee2e6;
      font-size: 0.8rem;
      display: flex;
      justify-content: space-around;
      gap: 12px;
    }

    .progression-winner {
      color: #198754;
    }

    .progression-loser {
      color: #dc3545;
    }

    /* Actions footer */
    .pm-match-card__actions {
      padding: 12px 16px;
      background: #f8f9fa;
      border-top: 1px solid #dee2e6;
      position: relative;
    }

    /* Edit teams button - floating in top-right corner */
    .btn-edit-teams {
      position: absolute;
      top: 8px;
      right: 8px;
      background: #ffc107;
      border: none;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      opacity: 0.7;
      z-index: 10;
      padding: 0;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .btn-edit-teams:hover {
      opacity: 1;
      transform: scale(1.1);
      background: #ffca2c;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    .btn-edit-teams .icon {
      width: 16px;
      height: 16px;
      fill: #212529;
    }

    .btn {
      display: inline-block;
      padding: 0.375rem 0.75rem;
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.5;
      text-align: center;
      text-decoration: none;
      border: 1px solid transparent;
      border-radius: 0.375rem;
      cursor: pointer;
      transition: all 0.15s ease-in-out;
      width: 100%;
    }

    .btn-primary {
      color: #fff;
      background-color: #0d6efd;
      border-color: #0d6efd;
    }

    .btn-primary:hover {
      background-color: #0b5ed7;
      border-color: #0a58ca;
    }

    .btn-outline-primary {
      color: #0d6efd;
      border-color: #0d6efd;
      background-color: transparent;
    }

    .btn-outline-primary:hover {
      color: #fff;
      background-color: #0d6efd;
      border-color: #0d6efd;
    }

    /* Info alert */
    .alert {
      margin: 12px 16px;
      padding: 12px;
      border-radius: 8px;
      font-size: 0.875rem;
    }

    .alert-info {
      background-color: #cff4fc;
      border: 1px solid #9eeaf9;
      color: #055160;
    }

    /* Badge status */
    .badge {
      display: inline-block;
      padding: 0.35em 0.65em;
      font-size: 0.75em;
      font-weight: 700;
      line-height: 1;
      color: #fff;
      text-align: center;
      white-space: nowrap;
      vertical-align: baseline;
      border-radius: 0.375rem;
    }

    .bg-success {
      background-color: #198754;
    }

    .bg-warning {
      background-color: #ffc107;
      color: #000;
    }

    .bg-info {
      background-color: #0dcaf0;
    }

    .bg-secondary {
      background-color: #6c757d;
    }

    .text-muted {
      color: #6c757d !important;
      font-style: italic;
    }

    /* Icons inline SVG */
    .icon {
      display: inline-block;
      width: 1em;
      height: 1em;
      vertical-align: -0.125em;
      fill: currentColor;
    }

    /* Responsive - Mobile */
    @media (max-width: 576px) {
      .pm-match-card {
        border-radius: 8px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
      }

      .pm-match-card__header {
        padding: 8px 12px;
        font-size: 0.85rem;
      }

      .pm-match-card__teams {
        min-height: 100px;
        padding: 0;
      }

      .pm-match-card__team {
        padding: 8px 10px;
        gap: 6px;
      }

      .pm-match-card__team--left {
        padding-right: 20px;
      }

      .pm-match-card__team--right {
        padding-left: 20px;
      }

      .pm-match-card__vs {
        width: 30px;
        height: 30px;
        font-size: 0.55rem;
      }

      .pm-match-card__team-name {
        font-size: 0.85rem;
        line-height: 1.3;
        letter-spacing: 0;
      }

      .pm-match-card__skill {
        font-size: 0.6rem;
        padding: 2px 4px;
      }

      .pm-match-card__score {
        font-size: 0.75rem;
        padding: 4px 8px;
      }

      .pm-match-card__score-container {
        padding: 0 12px 8px 12px;
        gap: 6px;
      }

      .pm-match-card__actions {
        padding: 8px 12px;
      }

      .pm-match-card__progression {
        padding: 8px 12px;
        font-size: 0.75rem;
        gap: 8px;
      }

      .btn-edit-teams {
        width: 28px;
        height: 28px;
        top: 6px;
        right: 6px;
      }

      .btn-edit-teams .icon {
        width: 14px;
        height: 14px;
      }

      .btn {
        padding: 0.25rem 0.5rem;
        font-size: 0.8rem;
      }
    }

    /* Dark mode support - Campo da padel notturno azzurro */
    :host([data-bs-theme="dark"]) .pm-match-card {
      background: #212529;
      border-color: #495057;
    }

    :host([data-bs-theme="dark"]) .pm-match-card__teams::before {
      background: rgba(79, 167, 219, 0.4); /* Azzurro più intenso per dark mode */
    }

    :host([data-bs-theme="dark"]) .pm-match-card__header {
      background: linear-gradient(165deg, #343a40 0%, #495057 100%);
      border-bottom-color: #495057;
    }

    :host([data-bs-theme="dark"]) .pm-match-card.completed .pm-match-card__header {
      background: linear-gradient(165deg, #1a4d2e 0%, #2d6a4f 100%);
    }

    :host([data-bs-theme="dark"]) .pm-match-card.scheduled .pm-match-card__header {
      background: linear-gradient(165deg, #664d03 0%, #997404 100%);
    }

    :host([data-bs-theme="dark"]) .pm-match-card.in_progress .pm-match-card__header {
      background: linear-gradient(165deg, #055160 0%, #087990 100%);
    }

    :host([data-bs-theme="dark"]) .pm-match-card__team-name {
      color: #f8f9fa;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    }

    :host([data-bs-theme="dark"]) .pm-match-card__team.winner .pm-match-card__team-name {
      color: #20c997;
      text-shadow: 0 2px 6px rgba(32, 201, 151, 0.4);
    }

    :host([data-bs-theme="dark"]) .pm-match-card__number {
      color: #f8f9fa;
    }

    :host([data-bs-theme="dark"]) .pm-match-card__progression,
    :host([data-bs-theme="dark"]) .pm-match-card__actions {
      background: #343a40;
      border-top-color: #495057;
    }

    :host([data-bs-theme="dark"]) .btn-edit-teams {
      background: #ffc107;
    }

    :host([data-bs-theme="dark"]) .btn-edit-teams:hover {
      background: #ffca2c;
    }

    :host([data-bs-theme="dark"]) .btn-edit-teams .icon {
      fill: #000;
    }

    :host([data-bs-theme="dark"]) .alert-info {
      background-color: #055160;
      border-color: #0a6e7e;
      color: #9eeaf9;
    }

    :host([data-bs-theme="dark"]) .btn-primary {
      background-color: #0d6efd;
      border-color: #0d6efd;
    }

    :host([data-bs-theme="dark"]) .btn-outline-primary {
      color: #6ea8fe;
      border-color: #6ea8fe;
    }

    :host([data-bs-theme="dark"]) .btn-outline-primary:hover {
      background-color: #0d6efd;
      border-color: #0d6efd;
      color: #fff;
    }

    /* Media query for system dark mode preference - Campo notturno azzurro */
    @media (prefers-color-scheme: dark) {
      :host(:not([data-bs-theme="light"])) .pm-match-card {
        background: #212529;
        border-color: #495057;
      }

      :host(:not([data-bs-theme="light"])) .pm-match-card__teams::before {
        background: rgba(79, 167, 219, 0.4); /* Azzurro più intenso per dark mode */
      }

    /* Explicit light mode - override system dark preference */
    :host([data-bs-theme="light"]) .pm-match-card {
      background: white;
      border-color: #dee2e6;
    }

    :host([data-bs-theme="light"]) .pm-match-card__header {
      background: linear-gradient(165deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom-color: #dee2e6;
    }

    :host([data-bs-theme="light"]) .pm-match-card.completed .pm-match-card__header {
      background: linear-gradient(165deg, #d1e7dd 0%, #a3cfbb 100%);
    }

    :host([data-bs-theme="light"]) .pm-match-card.scheduled .pm-match-card__header {
      background: linear-gradient(165deg, #fff3cd 0%, #ffe69c 100%);
    }

    :host([data-bs-theme="light"]) .pm-match-card.in_progress .pm-match-card__header {
      background: linear-gradient(165deg, #cff4fc 0%, #9eeaf9 100%);
    }

    :host([data-bs-theme="light"]) .pm-match-card__team-name,
    :host([data-bs-theme="light"]) .pm-match-card__number {
      color: #212529;
    }

    :host([data-bs-theme="light"]) .pm-match-card__progression,
    :host([data-bs-theme="light"]) .pm-match-card__actions {
      background: #f8f9fa;
      border-top-color: #dee2e6;
    }

    :host([data-bs-theme="light"]) .alert-info {
      background-color: #cff4fc;
      border-color: #9eeaf9;
      color: #055160;
    }

    :host([data-bs-theme="light"]) .btn-primary {
      background-color: #0d6efd;
      border-color: #0d6efd;
      color: #fff;
    }

    :host([data-bs-theme="light"]) .btn-outline-primary {
      color: #0d6efd;
      border-color: #0d6efd;
      background-color: transparent;
    }

    :host([data-bs-theme="light"]) .btn-outline-primary:hover {
      background-color: #0d6efd;
      border-color: #0d6efd;
      color: #fff;
    }
  `;

  // Properties
  @property({ type: Number, attribute: 'match-id' })
  matchId = 0;

  @property({ type: String, attribute: 'match-number' })
  matchNumber = '';

  @property({ type: String, attribute: 'match-icon' })
  matchIcon = '';

  @property({ type: String })
  status: MatchStatus = 'tbd';

  @property({ type: String, attribute: 'side1-name' })
  side1Name = '';

  @property({ type: String, attribute: 'side2-name' })
  side2Name = '';

  @property({ type: String, attribute: 'side1-skills' })
  side1Skills = '';

  @property({ type: String, attribute: 'side2-skills' })
  side2Skills = '';

  @property({ type: Boolean, attribute: 'side1-is-winner' })
  side1IsWinner = false;

  @property({ type: Boolean, attribute: 'side2-is-winner' })
  side2IsWinner = false;

  @property({ type: String, attribute: 'winner-score' })
  winnerScore = '';

  @property({ type: String, attribute: 'progression-winner' })
  progressionWinner = '';

  @property({ type: String, attribute: 'progression-loser' })
  progressionLoser = '';

  @property({ type: Boolean })
  editable = false;

  @property({ type: String, attribute: 'edit-url' })
  editUrl = '';

  @property({ type: String, attribute: 'info-message' })
  infoMessage = '';

  // Edit teams properties
  @property({ type: String })
  group = '';

  @property({ type: String, attribute: 'side1-player1-id' })
  side1Player1Id = '';

  @property({ type: String, attribute: 'side1-player1-name' })
  side1Player1Name = '';

  @property({ type: String, attribute: 'side1-player2-id' })
  side1Player2Id = '';

  @property({ type: String, attribute: 'side1-player2-name' })
  side1Player2Name = '';

  @property({ type: String, attribute: 'side2-player1-id' })
  side2Player1Id = '';

  @property({ type: String, attribute: 'side2-player1-name' })
  side2Player1Name = '';

  @property({ type: String, attribute: 'side2-player2-id' })
  side2Player2Id = '';

  @property({ type: String, attribute: 'side2-player2-name' })
  side2Player2Name = '';

  @property({ type: Number, attribute: 'round-number' })
  roundNumber?: number;

  @property({ type: String, attribute: 'data-refresh' })
  dataRefresh = '0';

  // Event listener cleanup
  private matchUpdatedListener?: EventListener;

  connectedCallback() {
    super.connectedCallback();
    this.setupMatchUpdateListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  /**
   * Setup listener per aggiornamenti real-time dei match
   */
  private setupMatchUpdateListener() {
    this.matchUpdatedListener = ((event: CustomEvent) => {
      const { matchId, status, winnerScore, side1IsWinner, side2IsWinner } = event.detail;
      
      // Aggiorna solo se è il nostro match
      if (matchId === this.matchId) {
        // Aggiorna le proprietà reattive
        this.status = status;
        this.winnerScore = winnerScore;
        this.side1IsWinner = side1IsWinner;
        this.side2IsWinner = side2IsWinner;
        
        // Forza il re-render
        this.requestUpdate();
        
        // Animazione per indicare l'aggiornamento
        this.highlightUpdate();
      }
    }) as EventListener;

    document.addEventListener('match-updated', this.matchUpdatedListener);
  }

  /**
   * Cleanup degli event listeners
   */
  private cleanup() {
    if (this.matchUpdatedListener) {
      document.removeEventListener('match-updated', this.matchUpdatedListener);
    }
  }

  /**
   * Animazione per evidenziare l'aggiornamento del match
   */
  private highlightUpdate() {
    const cardElement = this.shadowRoot?.querySelector('.pm-match-card') as HTMLElement;
    if (cardElement) {
      // Aggiungi classe per animazione
      cardElement.style.transition = 'all 0.3s ease-in-out';
      cardElement.style.transform = 'scale(1.02)';
      cardElement.style.boxShadow = '0 8px 25px rgba(25, 135, 84, 0.3)';
      cardElement.style.borderColor = '#20c997';
      
      // Rimuovi l'animazione dopo un breve periodo
      setTimeout(() => {
        cardElement.style.transform = '';
        cardElement.style.boxShadow = '';
        cardElement.style.borderColor = '';
        
        // Toast di conferma
        this.showUpdateToast();
      }, 600);
    }
  }

  /**
   * Mostra toast di conferma aggiornamento
   */
  private showUpdateToast() {
    const globalShowToast = (window as any).showToast;
    if (globalShowToast) {
      globalShowToast({
        message: `Match ${this.matchNumber} aggiornato!`,
        variant: 'success',
        duration: 2000
      });
    }
  }

  // Helper methods
  private getStatusBadge() {
    const statusMap = {
      completed: html`<span class="badge bg-success">Completato</span>`,
      scheduled: html`<span class="badge bg-warning">In Attesa</span>`,
      in_progress: html`<span class="badge bg-info">In Corso</span>`,
      tbd: html`<span class="badge bg-secondary">TBD</span>`,
    };
    return statusMap[this.status];
  }

  /**
   * Estrae un singolo valore di punteggio dal winnerScore
   * Format atteso: "6-3, 4-6, 7-5" o "6-3, 4-6"
   * Il formato è sempre side1_score-side2_score indipendentemente da chi vince
   */
  private getScoreValue(team: 'side1' | 'side2', setIndex: number): number {
    if (!this.winnerScore) return 0;

    const sets = this.winnerScore.split(',').map(s => s.trim());
    if (setIndex >= sets.length) return 0;

    const scores = sets[setIndex].split('-').map(s => parseInt(s.trim()));
    if (scores.length !== 2) return 0;

    // Formato consistente: sempre side1-side2 (posizioni fisse)
    // side1 → posizione 0, side2 → posizione 1
    if (team === 'side1') {
      return scores[0]; // Primo valore è sempre side1
    } else {
      return scores[1]; // Secondo valore è sempre side2
    }
  }

  private parseSkills(skillsString: string): SkillLevel[] {
    if (!skillsString) return [];
    return skillsString.split(',').map(s => s.trim() as SkillLevel);
  }

  private getSkillBadgeClass(skill: SkillLevel): string {
    const map = {
      P: 'skill-principiante',
      I: 'skill-intermedio',
      A: 'skill-avanzato',
      E: 'skill-esperto',
      PRO: 'skill-professionale',
    };
    return map[skill] || 'skill-principiante';
  }

  private getSkillFullName(skill: SkillLevel): string {
    const map = {
      P: 'Principiante',
      I: 'Intermedio',
      A: 'Avanzato',
      E: 'Esperto',
      PRO: 'Professionale',
    };
    return map[skill] || 'Principiante';
  }

  private renderSkillBadges(skillsString: string) {
    const skills = this.parseSkills(skillsString);
    if (skills.length === 0) return nothing;

    return skills.map(
      skill => html`
        <span 
          class="pm-match-card__skill ${this.getSkillBadgeClass(skill)}"
          title="${this.getSkillFullName(skill)}"
        >${skill}</span>
      `
    );
  }

  private renderIcon(name: string) {
    const icons: Record<string, any> = {
      hashtag: html`<svg class="icon" viewBox="0 0 24 24"><path d="M10 3L8 21h2l2-18h-2zm4 0l-2 18h2l2-18h-2zM3 10v2h18v-2H3zm0 4v2h18v-2H3z"/></svg>`,
      users: html`<svg class="icon" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>`,
      shield: html`<svg class="icon" viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>`,
      crown: html`<svg class="icon" viewBox="0 0 24 24"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/></svg>`,
      edit: html`<svg class="icon" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>`,
      plus: html`<svg class="icon" viewBox="0 0 24 24"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>`,
      info: html`<svg class="icon" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>`,
      question: html`<svg class="icon" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>`,
      'arrow-up': html`<svg class="icon" viewBox="0 0 24 24"><path d="M7 14l5-5 5 5z"/></svg>`,
      'arrow-down': html`<svg class="icon" viewBox="0 0 24 24"><path d="M7 10l5 5 5-5z"/></svg>`,
    };
    return icons[name] || nothing;
  }

  private handleEditClick(e: Event) {
    e.preventDefault();
    
    // Priority 1: Use edit URL if provided (legacy compatibility)
    if (this.editUrl) {
      window.location.href = this.editUrl;
      return;
    }

    // Priority 2: Use global openScoreModal if available (pm-score-modal integration)
    const openScoreModal = (window as any).openScoreModal;
    if (openScoreModal) {
      // Crea un elemento temporaneo con tutti i data attributes per il modal
      const tempButton = document.createElement('button');
      tempButton.setAttribute('data-match-id', this.matchId.toString());
      tempButton.setAttribute('data-match-number', this.matchNumber);
      tempButton.setAttribute('data-side1-name', this.side1Name);
      tempButton.setAttribute('data-side2-name', this.side2Name);
      tempButton.setAttribute('data-edit-mode', (this.status === 'completed').toString());
      
      // Aggiungi i punteggi se il match è completato
      if (this.status === 'completed' && this.winnerScore) {
        tempButton.setAttribute('data-side1-set1', this.getScoreValue('side1', 0).toString());
        tempButton.setAttribute('data-side2-set1', this.getScoreValue('side2', 0).toString());
        tempButton.setAttribute('data-side1-set2', this.getScoreValue('side1', 1).toString());
        tempButton.setAttribute('data-side2-set2', this.getScoreValue('side2', 1).toString());
        tempButton.setAttribute('data-side1-set3', this.getScoreValue('side1', 2).toString());
        tempButton.setAttribute('data-side2-set3', this.getScoreValue('side2', 2).toString());
      }
      
      openScoreModal(tempButton);
      return;
    }

    // Priority 3: Emit custom event for manual handling
    this.dispatchEvent(
      new CustomEvent('match-edit', {
        detail: { matchId: this.matchId },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Handler per il click sul pulsante di modifica coppie
   */
  private handleEditTeamsClick(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    const currentPlayers: CurrentPlayers = {
      side1_player1_id: this.side1Player1Id,
      side1_player1_name: this.side1Player1Name,
      side1_player2_id: this.side1Player2Id,
      side1_player2_name: this.side1Player2Name,
      side2_player1_id: this.side2Player1Id,
      side2_player1_name: this.side2Player1Name,
      side2_player2_id: this.side2Player2Id,
      side2_player2_name: this.side2Player2Name,
    };

    // Emetti evento per aprire il modale di edit teams
    this.dispatchEvent(
      new CustomEvent('edit-pairs-requested', {
        detail: {
          matchId: this.matchId,
          group: this.group,
          roundNumber: this.roundNumber,
          currentPlayers,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Renderizza il pulsante di modifica coppie (solo per match non completati e editabili)
   */
  private renderEditTeamsButton() {
    // Mostra solo se:
    // - Il match è editabile
    // - Il match non è completato
    // - Ci sono giocatori definiti
    if (!this.editable || this.status === 'completed' || !this.side1Player1Id) {
      return nothing;
    }

    return html`
      <button
        type="button"
        class="btn-edit-teams"
        @click=${this.handleEditTeamsClick}
        title="Modifica coppie"
        aria-label="Modifica coppie del match"
      >
        ${this.renderIcon('edit')}
      </button>
    `;
  }

  render() {
    const isCompleted = this.status === 'completed';
    const side1IsWinner = this.side1IsWinner;
    const side2IsWinner = this.side2IsWinner;
    const hasProgression = this.progressionWinner || this.progressionLoser;

    return html`
      <div class="pm-match-card ${this.status}">
        <!-- Header -->
        <div class="pm-match-card__header">
          <span class="pm-match-card__number">
            ${this.matchIcon ? this.renderIcon(this.matchIcon) : nothing}
            ${this.matchNumber}
          </span>
          ${this.getStatusBadge()}
        </div>

        <!-- Teams Container with Diagonal Layout -->
        <div class="pm-match-card__teams">
          <!-- Linee del campo da padel -->
          <div class="pm-match-card__court-net"></div>
          <div class="pm-match-card__court-line pm-match-card__court-line--service-left"></div>
          <div class="pm-match-card__court-line pm-match-card__court-line--service-right"></div>
          <div class="pm-match-card__court-line pm-match-card__court-line--center-horizontal"></div>
          
          <!-- Coppia 1 - Left Side (Diagonale da sinistra a destra) -->
          <div
            class="pm-match-card__team pm-match-card__team--left ${side1IsWinner ? 'winner' : ''} ${side2IsWinner ? 'loser' : ''}"
          >
            <div class="pm-match-card__team-name-row">
              ${this.side1Skills
                ? html`
                    <div class="pm-match-card__team-skills">
                      ${this.renderSkillBadges(this.side1Skills)}
                    </div>
                  `
                : nothing}
              <div class="pm-match-card__team-name">${this.side1Name || 'TBD'}</div>
            </div>
          </div>

          <!-- Coppia 2 - Right Side (Diagonale da destra a sinistra) -->
          <div
            class="pm-match-card__team pm-match-card__team--right ${side2IsWinner ? 'winner' : ''} ${side1IsWinner ? 'loser' : ''}"
          >
            <div class="pm-match-card__team-name-row">
              <div class="pm-match-card__team-name">${this.side2Name || 'TBD'}</div>
              ${this.side2Skills
                ? html`
                    <div class="pm-match-card__team-skills">
                      ${this.renderSkillBadges(this.side2Skills)}
                    </div>
                  `
                : nothing}
            </div>
          </div>

          <!-- VS Divider - Absolute Centered on Diagonal -->
          <div class="pm-match-card__vs-container">
            <div class="pm-match-card__vs">VS</div>
          </div>
        </div>

        <!-- Score Container -->
        ${this.winnerScore
          ? html`
              <div class="pm-match-card__score-container">
                <span class="pm-match-card__score">${this.winnerScore}</span>
              </div>
            `
          : nothing}

        <!-- Progression Info (Double Elimination) -->
        ${hasProgression
          ? html`
              <div class="pm-match-card__progression">
                ${this.progressionWinner
                  ? html`
                      <small class="progression-winner">
                        ${this.renderIcon('arrow-up')} ${this.progressionWinner}
                      </small>
                    `
                  : nothing}
                ${this.progressionLoser
                  ? html`
                      <small class="progression-loser">
                        ${this.renderIcon('arrow-down')} ${this.progressionLoser}
                      </small>
                    `
                  : nothing}
              </div>
            `
          : nothing}

        <!-- Info Message (TBD) -->
        ${this.infoMessage
          ? html`
              <div class="alert alert-info">
                ${this.renderIcon('info')} ${this.infoMessage}
              </div>
            `
          : nothing}

        <!-- Actions -->
        ${this.editable
          ? html`
              <div class="pm-match-card__actions">
                <!-- Edit teams button (floating) -->
                ${this.renderEditTeamsButton()}
                
                <!-- Score button (full width) -->
                <button
                  class="btn ${isCompleted ? 'btn-outline-primary' : 'btn-primary'}"
                  data-bs-target="#scoreModal"
                  data-match-id="${this.matchId}"
                  data-match-number="${this.matchNumber}"
                  data-side1-name="${this.side1Name}"
                  data-side2-name="${this.side2Name}"
                  data-edit-mode="${isCompleted}"
                  ${isCompleted && this.winnerScore
                    ? html`
                        data-side1-set1="${this.getScoreValue('side1', 0)}"
                        data-side2-set1="${this.getScoreValue('side2', 0)}"
                        data-side1-set2="${this.getScoreValue('side1', 1)}"
                        data-side2-set2="${this.getScoreValue('side2', 1)}"
                        data-side1-set3="${this.getScoreValue('side1', 2)}"
                        data-side2-set3="${this.getScoreValue('side2', 2)}"
                      `
                    : nothing}
                  @click=${this.handleEditClick}
                >
                  ${this.renderIcon(isCompleted ? 'edit' : 'plus')}
                  ${isCompleted ? 'Modifica Punteggi' : 'Inserisci Punteggi'}
                </button>
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-match-card': PmMatchCard;
  }
}
